


















































































import Vue from "vue";
import { IpaEvaluated } from "@joyz/app-core/models/typography";
import { MicConfig } from "@joyz/app-core/utils/microphone";
import {
  DRILL_PRONUNCIATION_TIMEOUT,
  LOGGER_EVENTS,
  THRESOLD_PETT_ERROR_COUNT
} from "@tt-app/constants";
import {
  ApiDrillPronunciation,
  DrillPronunciationAnswer,
  DrillPronunciationPhonemeEvaluation,
  DrillQuestionAndAnswers,
  DrillLearningStatus,
  LoggerEventDetails
} from "@tt-app/models";
import {
  detectSilence,
  uploadPronunciationDrillAudioToS3,
  evaluatePronunciation
} from "@tt-app/utils";
import { ApiPettURL } from "@tt-app/api/pett";

import DictionarySentenceModal from "@tt-app/components/exercise/SingleSentenceReading/DictionarySentenceModal.vue";
import TtMic from "./TtMic.vue";

import IconSpeaker from "@joyz/app-core/assets/icons/default/Speaker.svg";
import IconUserVoice from "@joyz/app-core/assets/icons/default/UserVoice.svg";
import IconCorrect from "@joyz/app-core/assets/icons/Correct.svg";
import IconIncorrect from "@joyz/app-core/assets/icons/Incorrect.svg";
import IconDictionary from "@joyz/app-core/assets/icons/default/Dictionary.svg";
import { AudioDecoderPlayer } from "@joyz/app-core/utils/audio";
import { audioContext } from "@tt-app/utils/audio/audioContext";

interface Data {
  loading: boolean;
  icons: { [key: string]: string };
  micStartPressSuccessful: boolean | null;
  pettError: string;
  pettErrorCount: number;
  recording: boolean;
  recordingTimeout?: number;
  temporaryEvaluation?: IpaEvaluated[];

  audioElement?: HTMLAudioElement;
  audioProgress: number;
  dictionaryModal: {
    open: boolean;
  };
  playingElement?: any;
  silenceDetected: boolean;
}

interface Methods {
  dictionaryModalCloseHandler: () => void;
  dictionaryModalOpenHandler: () => void;
  handleAudioPlaybackError: (
    loggerEvent: LoggerEventDetails,
    playbackError: { occurred: boolean }
  ) => (error: any) => void;
  handleMicStartPress: () => void;
  handleMicStopPress: () => void;
  onMicStarted: () => void;
  onMicStopped: (audioBlobs: Blob[]) => Promise<void>;
  onMicTimeout: () => void;
  onSilenceDetected: () => void;
  playUserAudio: () => void;
  playWordAudio: () => void;
  resetData: () => void;
  showTemporaryIpa: (evaluation: DrillPronunciationPhonemeEvaluation[]) => void;
  exampleAudioButtonClickHandler: (event: any) => void;
  startAudioPlayback: (event: any) => void;
  timeUpdateHandler: (event: any) => void;
}

interface Computed {
  dictionaryAvailable: boolean;
  drillQuestion: ApiDrillPronunciation;
  ipa: IpaEvaluated[];
  learnStatus: number;
  learnStatusDecrease: boolean | undefined;
  learnStatusIncrease: boolean | undefined;
  micConfig: MicConfig;
  msgTapToRecord: string;
}

interface Props {
  audioDecoderPlayer: AudioDecoderPlayer;
  drillExercise: DrillQuestionAndAnswers;
  isLoading: boolean;
}

export default Vue.extend<Data, Methods, Computed, Props>({
  name: "DrillQuestionPronunciation",
  components: { DictionarySentenceModal, TtMic },

  props: {
    audioDecoderPlayer: { type: Object as () => AudioDecoderPlayer, required: true },
    drillExercise: { type: Object as () => DrillQuestionAndAnswers, required: true },
    isLoading: { type: Boolean, default: false }
  },

  mounted() {
    this.audioElement = this.$refs.audio as HTMLAudioElement;
  },

  data() {
    return {
      audioElement: undefined,
      audioProgress: 0,
      dictionaryModal: {
        open: false
      },
      loading: false,
      icons: {
        dictionary: IconDictionary,
        speaker: IconSpeaker,
        userVoice: IconUserVoice,
        correct: IconCorrect,
        incorrect: IconIncorrect
      },
      micStartPressSuccessful: null,
      pettError: "",
      pettErrorCount: 0,
      recording: false,
      recordingTimeout: undefined,
      silenceDetected: false,
      temporaryEvaluation: undefined
    };
  },

  computed: {
    dictionaryAvailable() {
      const wordDefined =
        (this.drillExercise.question as ApiDrillPronunciation).word.definition !== null;

      return wordDefined;
    },

    drillQuestion() {
      return this.drillExercise.question as ApiDrillPronunciation;
    },

    ipa() {
      const lastAnswer = this.drillExercise.lastAnswer;

      if (lastAnswer) {
        return (lastAnswer.submittedAnswer as DrillPronunciationAnswer).evaluation.map(
          (e): IpaEvaluated => ({ text: e.ipa, isCorrect: e.isCorrect })
        );
      }

      return [];
    },

    learnStatus() {
      const getStatus = (status: DrillLearningStatus): number => (status !== null ? status : -1);

      return this.drillExercise.lastAnswer
        ? getStatus(this.drillExercise.lastAnswer.learnStatus.newLearnStatus)
        : getStatus(this.drillExercise.question.learnedStatus);
    },

    learnStatusDecrease() {
      return this.drillExercise.lastAnswer
        ? this.drillExercise.lastAnswer.learnStatus.hasIncreased === false
        : false;
    },

    learnStatusIncrease() {
      return this.drillExercise.lastAnswer
        ? this.drillExercise.lastAnswer.learnStatus.hasIncreased === true
        : false;
    },

    micConfig() {
      return {
        impl: "ScriptNodeRecorder",
        options: {
          audioCtx: audioContext,
          onRecordingStarted: () => this.onMicStarted(),
          onRecordingStopped: blobs => this.onMicStopped(blobs),
          onSilenceDetected: () => this.onSilenceDetected()
        }
      };
    },

    msgTapToRecord() {
      if (this.pettError) {
        return this.pettError;
      }

      if (this.drillExercise.lastAnswer) {
        return this.drillExercise.lastAnswer.correction.isCorrect
          ? this.$__("drill.pronunciation.mic.success")
          : this.$__("drill.pronunciation.mic.tap-to-try-again");
      }

      return this.$__("drill.pronunciation.mic.tap-to-start");
    }
  },

  watch: {
    "drillExercise.question.uuid": {
      handler(newVal: string, oldVal: string) {
        if (newVal !== oldVal) {
          this.pettError = "";
          this.pettErrorCount = 0;
          window.clearInterval(this.recordingTimeout);

          const mainContainer = document.querySelector(".drill-main-container");
          if (mainContainer) {
            mainContainer.scrollTo(0, 0);
          }
        }
      },
      immediate: false
    },

    ipa(newValue) {
      if (newValue.length) {
        this.temporaryEvaluation = undefined;
      }
    }
  },

  beforeDestroy() {
    window.clearInterval(this.recordingTimeout);
  },

  methods: {
    dictionaryModalCloseHandler() {
      this.$emit("logger-event", LOGGER_EVENTS.USER.DICTIONARY_MODAL_CLOSE.SUCCESS);
      this.dictionaryModal.open = false;
    },
    dictionaryModalOpenHandler() {
      if (this.recording) return;

      this.$emit("logger-event", LOGGER_EVENTS.USER.DICTIONARY_MODAL_OPEN.SUCCESS);
      this.dictionaryModal.open = true;
    },
    handleAudioPlaybackError(loggerEvent, playbackError) {
      return customError => {
        playbackError.occurred = true;
        this.$emit("logger-event", loggerEvent, {
          error: customError.error,
          url: customError.audioUrl
        });
      };
    },
    handleMicStartPress() {
      this.$emit("logger-event", LOGGER_EVENTS.USER.USER_AUDIO_RECORD_START.SUCCESS);
      this.micStartPressSuccessful = true;
    },
    handleMicStopPress() {
      this.$emit("logger-event", LOGGER_EVENTS.USER.USER_AUDIO_RECORD_STOP.SUCCESS);
    },
    onMicStarted() {
      const ttMic: any = this.$refs.microphone;
      const jzMic: any = ttMic.$refs.microphone;
      const micHandler = jzMic.mic;

      if (micHandler.errors.length > 0) {
        if (this.micStartPressSuccessful) {
          this.$emit(
            "logger-event",
            LOGGER_EVENTS.APPLICATION.AUDIO_RECORD_FOLLOWING_USER_AUDIO_RECORD_START.FAILURE,
            {
              error: {
                message: JSON.stringify(micHandler?.error)
              }
            }
          );
        }

        this.$emit("logger-event", LOGGER_EVENTS.APPLICATION.AUDIO_RECORD_START.FAILURE, {
          error: {
            message: JSON.stringify(micHandler?.error)
          }
        });

        this.micStartPressSuccessful = null;
        return;
      }

      this.$emit("logger-event", LOGGER_EVENTS.APPLICATION.AUDIO_RECORD_START.SUCCESS);
      this.$emit(
        "logger-event",
        LOGGER_EVENTS.APPLICATION.AUDIO_RECORD_FOLLOWING_USER_AUDIO_RECORD_START.SUCCESS
      );
      this.audioDecoderPlayer.stop();

      this.pettError = "";
      this.recording = true;
      this.micStartPressSuccessful = null;

      this.$emit("recording-started");
      this.recordingTimeout = window.setTimeout(() => {
        const mic = this.$refs.microphone as any;
        if (mic) {
          mic.stopRecording();
        }
        this.$emit("recording-timeout");
      }, DRILL_PRONUNCIATION_TIMEOUT);
    },

    async onMicStopped(audioBlob) {
      this.$emit("logger-event", LOGGER_EVENTS.APPLICATION.AUDIO_RECORD_STOP.SUCCESS);
      window.clearInterval(this.recordingTimeout);
      this.loading = true;

      if (audioBlob.length === 0) {
        return;
      }

      const firstBlob = audioBlob[0];

      const silenceDetected = await detectSilence(firstBlob);

      if (silenceDetected) {
        this.onSilenceDetected();
        this.resetData();
        this.$emit("logger-event", LOGGER_EVENTS.APPLICATION.AUDIO_RECORDED.FAILURE, {
          error: {
            message: "Silence detected"
          }
        });
        this.$emit("on-user-answer", undefined);
        return;
      }

      this.silenceDetected = false;

      this.$emit("logger-event", LOGGER_EVENTS.APPLICATION.AUDIO_RECORDED.SUCCESS);

      const audioUrlPromise = uploadPronunciationDrillAudioToS3(firstBlob);
      let audioUrl;

      try {
        this.$emit("logger-event", LOGGER_EVENTS.APPLICATION.PETT_REQUEST.SUCCESS, {
          url: ApiPettURL
        });
        const evaluation = await evaluatePronunciation(
          firstBlob,
          this.drillQuestion.word.spelling,
          this.drillQuestion.word.ipa
        );
        this.$emit("logger-event", LOGGER_EVENTS.APPLICATION.PETT_RESPONSE.SUCCESS, {
          url: ApiPettURL
        });

        this.showTemporaryIpa(evaluation);

        audioUrl = await audioUrlPromise;

        const answer: DrillPronunciationAnswer = {
          type: "DRILL_PRONUNCIATION",
          uuid: this.drillExercise.question.uuid,
          evaluation,
          audioUrl,
          audioBlob: firstBlob
        };
        this.$emit("on-user-answer", answer);

        // reset PETT error count on successful evaluation
        this.pettErrorCount = 0;
      } catch (err) {
        this.$emit("logger-event", LOGGER_EVENTS.APPLICATION.PETT_RESPONSE.FAILURE, {
          error: err,
          url: ApiPettURL
        });
        // PETT error handling
        this.pettError = this.$__("drill.pett.error");
        this.pettErrorCount++;

        if (this.pettErrorCount >= THRESOLD_PETT_ERROR_COUNT) {
          audioUrl = await audioUrlPromise;

          const answer: DrillPronunciationAnswer = {
            type: "DRILL_PRONUNCIATION",
            uuid: this.drillExercise.question.uuid,
            evaluation: this.drillQuestion.word.ipa.map(
              (phoneme): DrillPronunciationPhonemeEvaluation => ({
                ipa: phoneme,
                isCorrect: false
              })
            ),
            audioUrl,
            audioBlob: firstBlob,
            pettErrorCount: this.pettErrorCount
          };
          this.$emit("on-user-answer", answer);
        } else {
          this.$emit("on-pett-error");
        }
      } finally {
        // Actions to perform regardless of PETT status
        this.resetData();
      }
    },

    onMicTimeout() {
      // nothing for the moment
    },

    async playUserAudio() {
      if (this.recording) {
        return;
      }

      if (!this.drillExercise.lastAnswer) {
        return;
      }

      const lastAnswer = this.drillExercise.lastAnswer.submittedAnswer as DrillPronunciationAnswer;
      const lastAnswerAudio = lastAnswer ? lastAnswer.audioBlob : undefined;
      if (!lastAnswerAudio) {
        return;
      }

      const audioUrl = URL.createObjectURL(lastAnswerAudio);

      this.$emit("play-user-audio", { audioUrl });
    },

    async playWordAudio() {
      if (this.recording) {
        return;
      }

      const audioUrl = this.drillQuestion.word.audio;

      this.$emit("play-example-audio", { audioUrl });
    },

    resetData() {
      const mic = this.$refs.microphone as any;
      if (mic) {
        mic.resetData();
      }

      this.loading = false;
      this.recording = false;
      this.$emit("recording-stopped");
    },

    showTemporaryIpa(evaluation) {
      this.temporaryEvaluation = evaluation.map(
        (e): IpaEvaluated => ({ text: e.ipa, isCorrect: e.isCorrect })
      );
    },

    onSilenceDetected() {
      this.silenceDetected = true;
    },

    exampleAudioButtonClickHandler(event) {
      this.startAudioPlayback(event);
    },

    startAudioPlayback(event) {
      this.playingElement = event.currentTarget;

      const audioUrl = this.playingElement.getAttribute("src");

      this.$emit("play-example-audio", { audioUrl });
    },

    timeUpdateHandler(event) {
      this.audioProgress = (event.target.currentTime / event.target.duration) * 100;
    }
  }
});
